// src/components/Sections/HeroSection.tsx
import React from 'react';
import { ArrowRight, Code2, Cloud, Shield } from 'lucide-react';
import { useAppContext } from '../../contexts/AppContext';

const HeroSection: React.FC = () => {
  const { state } = useAppContext();
  
  // Translation helper
  const t = (sv: string, en: string) => state.language === 'sv' ? sv : en;

  const stats = [
    {
      number: 25,
      suffix: '+',
      label: t('års erfarenhet', 'years experience'),
      icon: Shield
    },
    {
      number: 14,
      suffix: '',
      label: t('år .NET-expertis', 'years .NET expertise'),
      icon: Code2
    },
    {
      number: 12,
      suffix: '+',
      label: t('tekniska ramverk', 'technical frameworks'),
      icon: Cloud
    }
  ];

  const techStack = ['.NET Core', 'Azure', 'React', 'TypeScript', 'Docker', 'Kubernetes', 'SQL Server'];

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-indigo-950">
      {/* Animated background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-blue-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute top-1/3 right-1/4 w-72 h-72 bg-indigo-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-1/4 left-1/2 w-72 h-72 bg-purple-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center">
          <div className="inline-flex items-center px-4 py-2 bg-blue-100 dark:bg-blue-900/30 rounded-full mb-6">
            <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
              {t('Senior Full Stack-utvecklare', 'Senior Full Stack Developer')}
            </span>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold text-gray-900 dark:text-white mb-6">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600">
              Gunnar Siréus
            </span>
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 mb-8 max-w-3xl mx-auto">
            {t(
              'Från legacy till cloud - jag bygger broar mellan teknologier med 25 års erfarenhet inom systemutveckling',
              'From legacy to cloud - I build bridges between technologies with 25 years of experience in system development'
            )}
          </p>

          <p className="text-lg text-gray-600 dark:text-gray-400 mb-12 max-w-2xl mx-auto">
            {t(
              'Specialiserad på Microsoft-stacken med Azure, .NET Core och moderna JavaScript-ramverk. Clean Code och SOLID är principer jag lever efter.',
              'Specialized in the Microsoft stack with Azure, .NET Core and modern JavaScript frameworks. Clean Code and SOLID are principles I live by.'
            )}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <button
              onClick={() => scrollToSection('contact')}
              className="inline-flex items-center px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200 transform hover:scale-105"
            >
              {t('Diskutera ditt projekt', 'Discuss your project')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>
            <button
              onClick={() => scrollToSection('case-studies')}
              className="inline-flex items-center px-8 py-4 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-white font-medium rounded-lg border-2 border-gray-200 dark:border-gray-700 transition-colors duration-200"
            >
              {t('Se tidigare uppdrag', 'View previous projects')}
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div
                  key={index}
                  className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300"
                >
                  <div className="flex flex-col items-center">
                    <Icon className="h-8 w-8 text-blue-600 dark:text-blue-400 mb-3" />
                    <div className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
                      <span className="tabular-nums">
                        {stat.number}
                      </span>
                      <span className="text-blue-600 dark:text-blue-400">
                        {stat.suffix}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 text-center">
                      {stat.label}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Tech stack badges */}
          <div className="mt-12 flex flex-wrap justify-center gap-3">
            {techStack.map((tech) => (
              <span
                key={tech}
                className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-full text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                {tech}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;